import { create } from 'zustand'
import type { AppNotification, LoadState, NotificationType } from '@/types'
import { api, describeError } from '@/services/api'

interface NotificationState {
  status: LoadState
  error: string | null
  items: AppNotification[]
  /** Person the feed is scoped to — set once settings load. */
  personId: string | null
  panelOpen: boolean

  setPerson: (personId: string | null) => void
  load: () => Promise<void>
  togglePanel: (open?: boolean) => void
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  dismiss: (id: string) => Promise<void>
  respond: (id: string, action: 'accepted' | 'declined') => Promise<void>
  /** Admin simulation panel — pushes a notification into the live feed. */
  simulate: (input: {
    type: NotificationType
    title: string
    body: string
    priority?: AppNotification['priority']
    personId?: string | null
    link?: string
    relatedAssignmentId?: string
    relatedPlanId?: string
  }) => Promise<void>
}

function unread(items: AppNotification[]): number {
  return items.filter((n) => !n.isRead).length
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  status: 'idle',
  error: null,
  items: [],
  personId: null,
  panelOpen: false,

  setPerson(personId) {
    set({ personId })
  },

  async load() {
    set({ status: get().items.length ? 'success' : 'loading', error: null })
    try {
      const personId = get().personId
      const query = personId ? `?personId=${encodeURIComponent(personId)}` : ''
      const items = await api.get<AppNotification[]>(`/notifications${query}`)
      set({ items, status: 'success' })
    } catch (error) {
      set({ status: 'error', error: describeError(error) })
    }
  },

  togglePanel(open) {
    set({ panelOpen: open ?? !get().panelOpen })
  },

  async markRead(id) {
    const items = get().items.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    set({ items })
    try {
      await api.patch(`/notifications/${id}`, { isRead: true })
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  async markAllRead() {
    set({ items: get().items.map((n) => ({ ...n, isRead: true })) })
    try {
      await api.post('/notifications/read-all', { personId: get().personId })
    } catch (error) {
      set({ error: describeError(error) })
      await get().load()
    }
  },

  async dismiss(id) {
    set({ items: get().items.filter((n) => n.id !== id) })
    try {
      await api.delete(`/notifications/${id}`)
    } catch (error) {
      set({ error: describeError(error) })
      await get().load()
    }
  },

  async respond(id, action) {
    const items = get().items.map((n) =>
      n.id === id ? { ...n, isRead: true, actionResolved: true, actionTaken: action } : n,
    )
    set({ items })
    try {
      await api.patch(`/notifications/${id}`, { actionTaken: action })
    } catch (error) {
      set({ error: describeError(error) })
      await get().load()
    }
  },

  async simulate(input) {
    try {
      const created = await api.post<AppNotification>('/notifications', input)
      set({ items: [created, ...get().items], error: null })
    } catch (error) {
      set({ error: describeError(error) })
    }
  },
}))

/** Badge counter for the header bell. */
export const selectUnreadCount = (state: NotificationState) => unread(state.items)
